/**
 * Pasaporte digital del equipo.
 *
 * Es la pantalla que responde a la pregunta que hace un inspector:
 * **«demuéstrame qué se le ha hecho a este extintor»**. Reúne identidad,
 * ciclo de vida, plan de mantenimiento con su artículo, historial de
 * operaciones e historial de ubicaciones.
 *
 * Dos cosas que se enseñan aquí y en ningún otro sitio:
 *
 * - **El contador de retimbrados.** Un extintor no tiene fecha de
 *   caducidad: tiene tres retimbrados y, cuando se agotan, no vuelve al
 *   servicio. Verlo antes de mandarlo al taller ahorra un viaje inútil.
 * - **Cada obligación con su artículo.** Es la regla de oro del proyecto
 *   hecha pantalla: nada normativo sin trazar.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSession } from '../../session/session-context.js';
import { ApiError } from '../../session/api-error.js';
import { DatumGroup, Datum, DetailShell, type Tab } from './detail-shell.js';
import { dayOf } from '../../table/format.js';

interface PlanItem {
  ruleCode: string;
  operationName: string;
  periodicityLabel: string;
  allowedPerformer: string;
  articleRef: string;
  sourceKind: string;
  uneReference: string | null;
  baseDate: string | null;
  dueDate: string | null;
  warnings: string[];
}

interface Lifecycle {
  kind: string;
  nextPressureTestDue?: string | null;
  nextOperation?: string;
  remainingRestampings?: number;
  endOfServiceDate?: string | null;
  exhausted?: boolean;
  shouldReplaceInsteadOfRetesting?: boolean;
  articleRef?: string;
  matrixRefs?: string[];
  warnings?: string[];
  endOfLifeDate?: string | null;
  nextVerificationDue?: string | null;
}

interface Passport {
  id: string;
  internalCode: string;
  qrToken: string | null;
  typeCode: string;
  systemCode: string;
  serialNumber: string | null;
  // Ficha del extintor
  brand: string | null;
  extinguisherKind: string | null;
  capacity: string | null;
  capacityUnit: string | null;
  manufacturingDate: string | null;
  stampingDate: string | null;
  restamping1On: string | null;
  restamping2On: string | null;
  restamping3On: string | null;
  lastMaintenanceDate: string | null;
  nextDueDate: string | null;
  status: string;
  lifecycle: Lifecycle;
  plan: PlanItem[];
  history: Array<Record<string, unknown>>;
  locationHistory: Array<{
    moved_at: string;
    previous_location_id: string | null;
    new_location_id: string | null;
    reason: string;
  }>;
}

const STATUS: Record<string, { label: string; tone: string; icon: string }> = {
  EN_SERVICIO: { label: 'En servicio', tone: 'ok', icon: '✓' },
  EN_TALLER: { label: 'En taller', tone: 'info', icon: '⚙' },
  PROVISIONAL_INSTALADO: { label: 'Provisional instalado', tone: 'info', icon: '⇄' },
  NO_LOCALIZADO: { label: 'No localizado', tone: 'warn', icon: '?' },
  INACCESIBLE: { label: 'Inaccesible', tone: 'warn', icon: '⚠' },
  RETIRADO: { label: 'Retirado', tone: 'off', icon: '•' },
  BAJA: { label: 'De baja', tone: 'off', icon: '•' },
};

const TODAY = new Date().toISOString().slice(0, 10);

const AGENTES: Record<string, string> = {
  ABC: 'Polvo ABC',
  BC: 'Polvo BC',
  CO2: 'CO₂',
  HIDRICO: 'Hídrico',
  ESPUMA: 'Espuma',
  CLASE_D: 'Clase D — metales',
  CLASE_F: 'Clase F — cocinas',
};

export function EquipmentDetailScreen(): React.ReactNode {
  const { api } = useSession();
  const { id } = useParams<{ id: string }>();

  const [passport, setPassport] = useState<Passport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    api
      .get<Passport>(`/equipment/${id}/passport`)
      .then((data) => {
        if (!cancelled) setPassport(data);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(caught instanceof ApiError ? caught.message : 'No se ha podido cargar el equipo.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, id]);

  const status = STATUS[passport?.status ?? ''] ?? { label: passport?.status ?? '—', tone: 'off', icon: '•' };
  const life = passport?.lifecycle;
  const isExtinguisher = life?.kind === 'EXTINGUISHER';

  const tabs: Tab[] = [
    {
      id: 'identidad',
      label: 'Identidad',
      render: () => (
        <DatumGroup title="Datos del equipo">
          <Datum label="Código interno">{passport?.internalCode}</Datum>
          <Datum label="Tipo">{passport?.typeCode}</Datum>
          <Datum label="Sistema">{passport?.systemCode}</Datum>
          <Datum label="Número de serie">{passport?.serialNumber}</Datum>
          <Datum label="Marca">{passport?.brand}</Datum>
          <Datum label="Tipo de agente">
            {AGENTES[passport?.extinguisherKind ?? ''] ?? passport?.extinguisherKind}
          </Datum>
          <Datum label="Capacidad">
            {passport?.capacity
              ? `${Number(passport.capacity)} ${passport.capacityUnit === 'L' ? 'litros' : 'kg'}`
              : null}
          </Datum>
          <Datum label="Fabricación">{passport?.manufacturingDate}</Datum>
          <Datum label="Timbrado inicial">{passport?.stampingDate}</Datum>
          <Datum label="Retimbrado 1">{passport?.restamping1On}</Datum>
          <Datum label="Retimbrado 2">{passport?.restamping2On}</Datum>
          <Datum
            label="Retimbrado 3"
            help="Después del tercero el extintor no vuelve al servicio: procede sustitución."
          >
            {passport?.restamping3On}
          </Datum>
          <Datum label="Última revisión">{passport?.lastMaintenanceDate}</Datum>
          <Datum label="Próxima revisión">{passport?.nextDueDate}</Datum>
          <Datum label="Estado">{status.label}</Datum>
          <Datum
            label="Token QR"
            help="Opaco a propósito: el QR de un equipo no revela de qué cliente es."
          >
            {passport?.qrToken ? <code className="code">{passport.qrToken}</code> : null}
          </Datum>
        </DatumGroup>
      ),
    },
    {
      id: 'ciclo',
      label: 'Ciclo de vida',
      render: () => (
        <>
          {isExtinguisher && life && (
            <>
              {life.exhausted === true ? (
                <p className="screen__note screen__note--strong">
                  <strong>Retimbrados agotados.</strong> Este extintor ya no admite más pruebas de
                  presión: procede la sustitución, no otro retimbrado.{' '}
                  {life.articleRef && <em>({life.articleRef})</em>}
                </p>
              ) : life.shouldReplaceInsteadOfRetesting === true ? (
                <p className="screen__note screen__note--strong">
                  {/* Saberlo antes de retirarlo ahorra un viaje al taller
                      que acabaría en «no se puede hacer nada». */}
                  <strong>No conviene mandarlo a retimbrar.</strong> Por su ciclo, el siguiente paso
                  sería la sustitución. {life.articleRef && <em>({life.articleRef})</em>}
                </p>
              ) : null}

              <DatumGroup title="Pruebas de presión">
                <Datum
                  label="Próxima prueba"
                  help="Es un contador, no una fecha de caducidad (filas B-12 y B-13)."
                >
                  {life.nextPressureTestDue}
                </Datum>
                <Datum label="Operación que toca">
                  {life.nextOperation === 'STAMPING'
                    ? 'Timbrado inicial'
                    : life.nextOperation === 'RESTAMPING'
                      ? 'Retimbrado'
                      : 'Ninguna'}
                </Datum>
                <Datum label="Retimbrados restantes">
                  {life.remainingRestampings !== undefined ? (
                    <span className={life.remainingRestampings === 0 ? 'due due--late' : 'due'}>
                      {life.remainingRestampings} de 3
                    </span>
                  ) : null}
                </Datum>
                <Datum label="Fin de servicio previsto">{life.endOfServiceDate}</Datum>
              </DatumGroup>
            </>
          )}

          {!isExtinguisher && life && (
            <DatumGroup title="Ciclo de vida">
              <Datum label="Tipo de ciclo">{life.kind === 'NONE' ? 'Sin ciclo específico' : life.kind}</Datum>
              <Datum label="Próxima verificación">{life.nextVerificationDue}</Datum>
              <Datum label="Fin de vida útil">{life.endOfLifeDate}</Datum>
            </DatumGroup>
          )}

          {life?.warnings && life.warnings.length > 0 && (
            <p className="screen__note">
              {/* Los avisos del motor suelen ser «falta un dato»: decirlos
                  aquí evita que alguien confíe en un cálculo incompleto. */}
              {life.warnings.join(' · ')}
            </p>
          )}
        </>
      ),
    },
    {
      id: 'plan',
      label: 'Plan',
      count: passport?.plan.length ?? null,
      render: () =>
        (passport?.plan.length ?? 0) === 0 ? (
          <p className="screen__note">
            Sin plan calculado. Puede que no haya una versión de la normativa publicada, o que este
            tipo de equipo no tenga reglas aplicables.
          </p>
        ) : (
          <div className="scroller">
            <table className="table__grid table__grid--compact">
              <thead>
                <tr>
                  <th>Operación</th>
                  <th>Periodicidad</th>
                  <th>Vence</th>
                  <th>Quién puede hacerlo</th>
                  <th>Regla</th>
                  <th>Artículo</th>
                  <th>Fuente</th>
                </tr>
              </thead>
              <tbody>
                {passport?.plan.map((item) => {
                  const overdue = item.dueDate !== null && item.dueDate < TODAY;
                  return (
                    <tr key={`${item.ruleCode}-${item.operationName}`}>
                      <td>{item.operationName}</td>
                      <td>{item.periodicityLabel}</td>
                      <td>
                        {item.dueDate === null ? (
                          <span className="due--none">—</span>
                        ) : overdue ? (
                          <span className="due due--late">⚠ {item.dueDate}</span>
                        ) : (
                          <span className="due">{item.dueDate}</span>
                        )}
                      </td>
                      <td>
                        {item.allowedPerformer === 'MAINTAINER_OR_HOLDER' ? (
                          <span className="pill pill--info">Titular o mantenedora</span>
                        ) : (
                          <span className="pill pill--off">Sólo mantenedora</span>
                        )}
                      </td>
                      <td>{item.ruleCode}</td>
                      {/* La regla de oro del proyecto, hecha columna. */}
                      <td>{item.articleRef}</td>
                      <td>
                        {item.sourceKind === 'UNE' ? (
                          <span className="pill pill--warn">⚠ {item.uneReference ?? 'UNE'}</span>
                        ) : (
                          <span className="pill pill--ok">✓ {item.sourceKind}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ),
    },
    {
      id: 'historial',
      label: 'Historial',
      count: passport?.locationHistory.length ?? null,
      render: () =>
        (passport?.locationHistory.length ?? 0) === 0 ? (
          <p className="screen__note">
            Este equipo no se ha movido desde que se dio de alta. El historial de traslados es la
            trazabilidad de ubicación: no se edita, se añade.
          </p>
        ) : (
          <div className="scroller">
            <table className="table__grid table__grid--compact">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Motivo</th>
                  <th>Desde</th>
                  <th>Hasta</th>
                </tr>
              </thead>
              <tbody>
                {passport?.locationHistory.map((move, index) => (
                  <tr key={`${move.moved_at}-${index}`}>
                    <td>{dayOf(move.moved_at)}</td>
                    <td>{move.reason}</td>
                    <td>{move.previous_location_id ?? '—'}</td>
                    <td>{move.new_location_id ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ),
    },
  ];

  return (
    <DetailShell
      title={passport?.internalCode ?? 'Equipo'}
      subtitle={passport ? `${passport.typeCode} · ${passport.systemCode}` : undefined}
      badges={
        passport && <span className={`pill pill--${status.tone}`}>{`${status.icon} ${status.label}`}</span>
      }
      actions={
        <>
          <Link className="button button--quiet button--small" to={`/equipos/${id}/editar`}>
            Corregir datos
          </Link>{' '}
          <Link className="button button--quiet button--small" to="/equipos">
            Volver al listado
          </Link>
        </>
      }
      tabs={tabs}
      loading={loading}
      error={error}
    />
  );
}
